import classNames from 'classnames';
import { routerActions } from 'connected-react-router';
import dayjs from 'dayjs';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useSelector } from '../hook-utils';
import { Identifier, InstantMessageConversation, InstantMessageConversationRecipent, InstantMessagerParticipantEndpointScheme } from '../model';
import { actions } from '../store';
import { selectConfigProp, selectInstantMessagerSettings, selectMyself } from '../store/reducers/config';
import { selectAllMessages, getInstantMessageLoaded } from '../store/reducers/instantmessager';
import { selectLastInstantMessageView } from '../store/reducers/localSettings';
import { RelativeShortFormatDateTime } from './CallsPage';
import { ReactComponent as AddIcon } from './icons/add.svg';
import { ImProfilePhoto } from './IM/ImProfilePhoto';
import { InstantMessagerView } from './IM/InstantMessagerView';
import './InstantMessagerPage.scss';
import { Menu, MenuItem, MenuTarget, targetFromEvent } from './Menu';
import MoreButton from './MoreButton';
import ProfilePhoto from './ProfilePhoto';
import SidebarView from './SidebarView';

interface MenuState {
  target: MenuTarget
  item: InstantMessageConversation
}

export default function InstantMessagerPage() {
  console.log('Render InstantMessagerPage')

  const cache = useSelector(getInstantMessageLoaded)
  const [menuState, setMenuState] = useState<MenuState>()
  const dispatch = useDispatch()
  const mySelf = useSelector(selectMyself)
  const socketId = useSelector(s => selectConfigProp(s, 'socketId'))
  const messagers = useSelector(s => selectAllMessages(s))
  const lastSelectedInstantMassagerId = useSelector(selectLastInstantMessageView)
  const settings = useSelector(selectInstantMessagerSettings)

  function onMore(e: React.MouseEvent, item: InstantMessageConversation) {
    e.preventDefault()
    e.stopPropagation()
    setMenuState({ target: targetFromEvent(e), item })
  }
  
  useEffect(() => {
    if (lastSelectedInstantMassagerId) {
      return () => {
        dispatch(actions.requestLeaveConversation(lastSelectedInstantMassagerId))
      }
    }
  }, [dispatch, lastSelectedInstantMassagerId])

  useEffect(() => {
    if (cache || !mySelf || !socketId) return

    dispatch(actions.instantMessagerDataRequest(mySelf, socketId))

  }, [cache, dispatch, messagers, mySelf, socketId])

  if (!messagers || !mySelf || !settings) return null

  if (!settings.imEnabled) {
    dispatch(routerActions.replace('/'))
  }

  const selected = messagers.find(m => m.conversationId === lastSelectedInstantMassagerId)
  if (!selected && messagers.length > 0) {
    dispatch(actions.changeInstantmessageView(messagers[0].conversationId))
  }

  return <SidebarView value={selected} onChange={m => {
    if (m) dispatch(actions.changeInstantmessageView(m.conversationId))
  }} >
    <SidebarView.Sidebar className='InstantMessagerSideBar'>
      <SidebarView.Header>Conversations <div className='icon add-button' title='Create new conversation' onClick={() => dispatch(actions.requestNewConversation())}><AddIcon /></div></SidebarView.Header>
      <div className='InstantMessagerSideBarItems'>
        {messagers.map(m => <SidebarView.Item key={m.conversationId} value={m}>
          <ConversationEndPoints mySelf={mySelf} conversation={m} onMore={onMore} />
          {menuState?.item.conversationId === m.conversationId && <ConversationMenu {...menuState} onClose={() => setMenuState(undefined)} />}
        </SidebarView.Item>)}
      </div>
    </SidebarView.Sidebar>
    <SidebarView.Body>
      {selected && <InstantMessagerView id={selected.conversationId} />}
    </SidebarView.Body>
  </SidebarView>
}

function ConversationEndPoints({ mySelf, conversation, onMore }: { mySelf: Identifier, conversation: InstantMessageConversation, onMore: (e: React.MouseEvent, item: InstantMessageConversation) => void }) {
  //const participants = conversation.recipients.filter(p => !(p.endpointScheme === InstantMessagerParticipantEndpointScheme.IpfxExtension && p.endpointId === mySelf.id && p.endpointServer === mySelf.server))
  //ugly fix
  let participants: InstantMessageConversationRecipent[] = []

  if (conversation.recipients !== undefined) {
    for (let index = 0; index < conversation.recipients.length; index++) {
      const p = conversation.recipients[index]
      if (p.displayName !== '' && !(p.endpointScheme === InstantMessagerParticipantEndpointScheme.IpfxExtension && p.endpointId === mySelf.id && p.endpointServer === mySelf.server)) {
        participants.push({
          displayName: p.displayName,
          endpointId: p.endpointId,
          endpointScheme: p.endpointScheme,
          endpointServer: p.endpointServer,
        } as InstantMessageConversationRecipent)
      }
    }
  }

  for (let index = 0; index < conversation.participants.length; index++) {
    const p = conversation.participants[index]
    const x = participants.findIndex(y => y.endpointScheme === p.endpointScheme && y.endpointServer === p.endpointServer && y.endpointId === p.endpointId)
    if (x === -1 && !(p.endpointScheme === InstantMessagerParticipantEndpointScheme.IpfxExtension && p.endpointId === mySelf.id && p.endpointServer === mySelf.server)) {
      participants.push({
        displayName: p.displayName,
        endpointId: p.endpointId,
        endpointScheme: p.endpointScheme,
        endpointServer: p.endpointServer,
      } as InstantMessageConversationRecipent)
    }
  }

  let names = ''
  let photo = null
  const date = dayjs(conversation.lastUpdatedUtc)
  if (participants.length === 1) {
    names = participants[0].displayName
    photo = ImProfilePhoto(participants[0].displayName, participants[0].endpointScheme, participants[0].endpointServer, participants[0].endpointId, 'photo')
  } else {
    for (let index = 0; index < participants.length; index++) {
      names += participants[index].displayName + (index !== participants.length - 1 ? (index === participants.length - 2 ? ' and ' : ', ') : '')
    }
    photo = <ProfilePhoto className='photo' name='Mulitple' />
  }

  if (names === '') {
    names = 'No participants'
  }

  const unread = conversation.unreadMessagesTotal > 0 || conversation.unreadUrgentMessagesTotal > 0

  return <div className={classNames('endpoint', unread && 'unread')} title={names}>
    {photo}
    <div className='names'>{conversation.conversationTitle}</div>
    <div className='date'><RelativeShortFormatDateTime date={date.valueOf()} /></div>
    <div className='lastmessage'>message : {conversation.lastUnreadMessage}</div>
    <div className='onMore'>
      <MoreButton className='menu-button' onClick={e => {
        e.stopPropagation()
        onMore(e, conversation)
      }} />
    </div>
  </div>
}

function ConversationMenu({ item, target, onClose }: { item: InstantMessageConversation, target: MenuTarget, onClose(): void }) {
  const dispatch = useDispatch()

  return <Menu open={true} className='ConversationMenu' target={target || null} onClose={onClose} ignoreFocusEvents={true}>
    <MenuItem onClick={() => dispatch(actions.requestNewConversation())}>New Instant Message...</MenuItem>
    <MenuItem onClick={() => dispatch(actions.changeInstantmessageView(item.conversationId))}>Reply...</MenuItem>
    <MenuItem onClick={() => dispatch(actions.hideInstantmessage(item.conversationId))}>Hide conversation</MenuItem>
  </Menu>
}
